import { ColDef, ICellRendererParams, EditableCallbackParams } from 'ag-grid-community';
import { orderBy } from 'lodash';
import { createSelector } from 'reselect';
import { AgreementCell, PriceCell } from '../../../common/datagrid';

import {
    AgreementDto,
    AgreementStatus,
    AgreementType,
    ExternalPricelistDto,
    PriceChangeDirection,
    PricelistDto
} from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { createProductLookupSelector, includes, startsWith, toDateString, toLowerCase } from '../../../utils';
import { getUnfilteredExternalPricelists } from '../../externalpricelist/redux/externalPricelistSelectors';
import { getUnfilteredPricelists } from '../../pricelist/redux/pricelistSelectors';
import { getProducts } from '../../product/redux/productSelectors';
import { getStocks } from '../../stock/redux/stockSelectors';
import { getStockAmount } from '../../stock/stockFunctions';
import { ProductStock } from '../../stock/stockTypes';
import { getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { AgreementPriceRow } from '../agreementPriceTypes';
import { CheckboxRenderer } from '../CheckboxRenderer';
import { saveAgreementPriceDiscountRequest } from './agreementSlices';

export const getSelectedAgreementId = (state: RootState) => state.agreement.ui.agreementId;
export const getSelectedRefPricelistId = (state: RootState) => state.agreement.ui.refPricelistId;
export const getSelectedRefExtPricelistId = (state: RootState) => state.agreement.ui.refExtPricelistId;

export const getSelectedRefExtPrices = (state: RootState) => state.agreement.refExtPrices;
export const getSelectedRefPrices = (state: RootState) => state.agreement.refPrices;

export const getSelectedAgreementPricelistDate = (state: RootState) => state.agreement.ui.pricelistDate;
export const getSelectedAgreementWarehouseDate = (state: RootState) => state.agreement.ui.stockDate;

export const getUnfilteredAgreements = (state: RootState) => state.agreement.agreements;
export const getAgreementPrices = (state: RootState) => state.agreement.agreementPrices;
export const getColumnSettings = (state: RootState) => state.agreement.agreementPriceTable.columns;

export const getAgreementPriceTable = (state: RootState) => state.agreement.agreementPriceTable;

export const getSelectedAgreementWarehouse = (state: RootState) => state.agreement.ui.warehouseId;
export const getReservedAgreementPrices = (state: RootState) => state.agreement.reservedAgreementPrices;

export const getAgreementShowAllArticles = (state: RootState) => state.agreement.ui.showAllArticles;

export const getAgreementTable = (state: RootState) => state.agreement.agreementTable;
export const getAgreementUi = (state: RootState) => state.agreement.ui;

export const getSelectedRefPricelist = createSelector(
    [getSelectedRefPricelistId, getUnfilteredPricelists],
    (selectedRefPricelistId, pricelists) => {
        if (selectedRefPricelistId == null) {
            return null;
        }

        return pricelists[selectedRefPricelistId];
    }
);

export const getAgreements = createSelector(
    [getUnfilteredAgreements, getCurrentUserPermissions],
    (unfilteredAgreements, userPermissions) => {
        if (userPermissions.isAdmin) {
            return unfilteredAgreements;
        }

        const agreements: { [agreementId: number]: AgreementDto } = {};

        const userGroups = userPermissions.userGroups;

        for (const agreement of Object.values(unfilteredAgreements)) {
            if (agreement.permittedUserGroups.length === 0) {
                agreements[agreement.agreementId] = agreement;
            } else if (
                userGroups.filter(userGroup =>
                    agreement.permittedUserGroups.map(c => c.userGroupId).includes(userGroup.userGroupId!)
                ).length > 0
            ) {
                agreements[agreement.agreementId] = agreement;
            }
        }

        return agreements;
    }
);

export const getAgreementTableRows = createSelector(
    [getAgreements, getAgreementTable, getAgreementUi],
    (agreements, { filters }, { statusFilter }) => {
        const filteredRows = Object.values(agreements).filter(
            r =>
                includes(filters.name, r.name) &&
                startsWith(filters.createdBy, r.createdBy) &&
                includes(filters.validAt, toDateString(new Date(r.validFrom), 'dd/MM')) &&
                (statusFilter != null ? r.agreementStatus === statusFilter : true)
        );

        return orderBy(
            filteredRows,
            [o => (o.createdDate == null ? '!' : toLowerCase(o.createdDate))],
            'desc'
        );
    }
);

export const getSelectedRefExtPricelist = createSelector(
    [getSelectedRefExtPricelistId, getUnfilteredExternalPricelists],
    (selectedRefExtPricelistId, extPricelists) => {
        if (selectedRefExtPricelistId == null) {
            return null;
        }

        return extPricelists[selectedRefExtPricelistId];
    }
);

const getAgreementPriceProductLookup = createProductLookupSelector(getAgreementPrices);
const getSelectedRefPricesProductLookup = createProductLookupSelector(getSelectedRefPrices);
const getSelectedRefExtPricesLookup = createProductLookupSelector(getSelectedRefExtPrices);

const getPricesToBeSaved = (state: RootState) => state.agreement.pricesToBeSaved;

const commonColumnProps: Partial<ColDef<AgreementPriceRow>> = {
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: {
        suppressFilterButton: true
    }
};

const initialColumns: { [key: string]: ColDef<AgreementPriceRow> } = {
    isInactive: {
        field: 'isInactive',
        headerName: 'Inactive',
        width: 84,
        cellStyle: {
            verticalAlign: 'center'
        },
        cellRenderer: params =>
            CheckboxRenderer(
                params,
                params?.data?.agreementPriceId != null && params?.data.agreementPriceId > 0
            ),
        editable: params => params?.data?.agreementPriceId != null && params?.data.agreementPriceId > 0
    },
    productId: {
        field: 'productId',
        headerName: 'Product Id',
        width: 100,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        editable: false
    },
    productName: {
        field: 'productName',
        headerName: 'Product name',
        width: 200,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        editable: false
    },
    country: {
        field: 'country',
        headerName: 'Country',
        width: 82,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        editable: false
    },
    stock: {
        field: 'stock',
        headerName: 'Stock',
        width: 82,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        editable: false
    }
};

export const getSelectedAgreement = createSelector(
    [getSelectedAgreementId, getAgreements],
    (selectedAgreementId, agreements) => {
        if (selectedAgreementId != null) {
            return agreements[selectedAgreementId];
        } else {
            return null;
        }
    }
);

const createPriceColumn = (agreement: AgreementDto, showTooltips: boolean) =>
    ({
        field: 'price',
        headerName: 'Price',
        cellDataType: 'text',
        cellRenderer: ({ data: row }) =>
            PriceCell({
                priceId: row.agreementPriceId ?? undefined,
                price: row.priceToBeSaved ?? row.price ?? undefined,
                isSaving: row.priceToBeSaved != null,
                updatedBy: row.updatedBy,
                updatedDate: row.updatedDate,
                discountOpts: {
                    title: 'Warchest',
                    discountable:
                        row.price != null &&
                        row.price > 0 &&
                        agreement.agreementType === AgreementType.Warchest,
                    canWrite:
                        agreement.agreementStatus === AgreementStatus.New &&
                        row.reservedAgreementPriceIds.length === 0,
                    discount: row.discount ?? 0,
                    saveDiscountActionCreator: (agreementPriceId, discount) =>
                        saveAgreementPriceDiscountRequest({ agreementPriceId, discount, type: 'in' })
                },
                showTooltips: showTooltips
            }),
        sortable: true,
        editable: ({ node }: EditableCallbackParams<AgreementPriceRow>) => {
            return (
                (agreement.agreementStatus === AgreementStatus.New &&
                    node.data?.reservedAgreementPriceIds?.length === 0) ??
                false
            );
        }
    } as ColDef<AgreementPriceRow>);

const createExternalPriceColumn = (agreement: AgreementDto, showTooltips: boolean) =>
    ({
        field: 'extPrice',
        headerName: 'External Price',
        cellDataType: 'text',
        cellRenderer: ({ data: row }) =>
            PriceCell({
                priceId: row.agreementPriceId,
                price: row.extPriceToBeSaved ?? row.extPrice ?? undefined,
                discountOpts: {
                    discountable:
                        row.price != null && row.price > 0 && row.extPrice != null && row.extPrice > 0,
                    canWrite:
                        agreement.agreementStatus === AgreementStatus.New &&
                        row.agreementPriceId != null &&
                        row.price != null &&
                        row.price > 0 &&
                        row.extPrice != null &&
                        row.extPrice > 0,
                    discount: row.extDiscount ?? 0,
                    saveDiscountActionCreator: (agreementPriceId, discount) =>
                        saveAgreementPriceDiscountRequest({ agreementPriceId, discount, type: 'ext' })
                },
                isSaving: row.extPriceToBeSaved != null,
                updatedBy: row.updatedBy,
                updatedDate: row.updatedDate,
                showTooltips: showTooltips
            }),
        cellEditorParams: {
            onNavigation: e => {
                if (e.type === 'keydown' && ['ArrowUp', 'ArrowDown'].includes(e.nativeEvent.code)) {
                    return true;
                }

                return false;
            }
        },
        sortable: true,
        editable: true
    } as ColDef<AgreementPriceRow>);

const createRefPricelistColumn = (refPricelist: PricelistDto, showTooltips: boolean) => {
    return {
        field: 'refPrice',
        headerName: refPricelist.name,
        cellRenderer: ({ data: row }) => {
            return PriceCell({
                priceId: undefined,
                price: row.refPrice ?? undefined,
                defaultBgColor: 'rgba(244, 244, 244, .6)',
                defaultColor: 'rgba(183,183,183, 0.3)',
                showTooltips: showTooltips
            });
        },
        sortable: true,
        editable: false
    } as ColDef<AgreementPriceRow>;
};

const createRefExtPricelistColumn = (refExtPricelist: ExternalPricelistDto, showTooltips: boolean) => {
    return {
        field: 'refExtPrice',
        headerName: refExtPricelist.name,
        cellRenderer: ({ data: row }) => {
            return PriceCell({
                priceId: undefined,
                price: row.refExtPrice ?? undefined,
                defaultBgColor: 'rgba(244, 244, 244, .6)',
                defaultColor: 'rgba(183,183,183, 0.3)',
                showTooltips: showTooltips
            });
        },
        sortable: true,
        editable: false
    } as ColDef<AgreementPriceRow>;
};

const createCommentColumn = (agreement: AgreementDto) => {
    return {
        field: 'comment',
        headerName: 'Comment',
        editable: row =>
            row.data?.agreementPriceId != null &&
            row.data.price != null &&
            agreement.agreementStatus === AgreementStatus.New
    } as ColDef<AgreementPriceRow>;
};

export const getAgreementColumns = createSelector(
    [
        getSelectedAgreement,
        getSelectedRefPricelist,
        getSelectedRefExtPricelist,
        getColumnSettings,
        getAgreementPriceTable
    ],
    (agreement, refPricelist, refExtPricelist, columnKeys, agreementPriceTable) => {
        if (agreement == null) {
            return [] as ColDef<AgreementPriceRow>[];
        }

        const columnMap: { [key: string]: ColDef<AgreementPriceRow> } = {
            ...initialColumns,
            price: createPriceColumn(agreement, agreementPriceTable.showTooltips),
            comment: createCommentColumn(agreement)
        };

        columnMap.productId = {
            ...columnMap.productId,
            cellRenderer: ({ data: row }: ICellRendererParams<AgreementPriceRow>) =>
                AgreementCell({
                    value: String(row?.productId ?? 0),
                    agreementPriceIds: row?.reservedAgreementPriceIds ?? [],
                    priceType: 'in',
                    currentAgreementId: agreement.agreementId ?? 0
                })
        };

        columnMap.isInactive.editable = ({ node }: EditableCallbackParams<AgreementPriceRow>) => {
            const data = node as unknown as AgreementPriceRow;
            return (
                data?.agreementPriceId != null &&
                data?.agreementPriceId > 0 &&
                agreement.agreementStatus !== AgreementStatus.Inactive &&
                data?.reservedAgreementPriceIds.length === 0
            );
        };

        if (agreement.handleExternalPrices) {
            columnMap.extPrice = createExternalPriceColumn(agreement, agreementPriceTable.showTooltips);
        }

        if (refPricelist != null) {
            columnMap.refPrice = createRefPricelistColumn(refPricelist, agreementPriceTable.showTooltips);
        }

        if (refExtPricelist != null) {
            columnMap.refExtPrice = createRefExtPricelistColumn(
                refExtPricelist,
                agreementPriceTable.showTooltips
            );
        }

        return columnKeys
            .map(key => ({ ...columnMap[key.key], ...commonColumnProps }))
            .filter(k => !k.hide && k.field != undefined);
    }
);

export const getAgreementPriceRows = createSelector(
    [
        getProducts,
        getAgreementPriceProductLookup,
        getSelectedRefPricesProductLookup,
        getSelectedRefExtPricesLookup,
        getSelectedAgreementWarehouse,
        getAgreementPriceTable,
        getStocks,
        getSelectedAgreementWarehouseDate,
        getPricesToBeSaved,
        getReservedAgreementPrices,
        getAgreementShowAllArticles
    ],
    (
        products,
        agreementPrices,
        prices,
        externalPrices,
        selectedWarehouse,
        { filters, sorting },
        stocks,
        warehouseDate,
        pricesToBeSaved,
        reservedAgreementPrices,
        showAllArticles
    ) => {
        let productStocks: ProductStock = {};
        if (warehouseDate != null && selectedWarehouse != null) {
            productStocks = stocks[selectedWarehouse] ?? {};
        }

        function getStock(productId: number) {
            const productStockGrp = productStocks[productId];
            if (productStockGrp == null) {
                return null;
            }
            return getStockAmount(warehouseDate, productStockGrp);
        }

        const agreementPriceRows = Object.values(products)
            .map(
                p =>
                    [
                        p,
                        getStock(p.productId),
                        agreementPrices[p.productId],
                        pricesToBeSaved[p.productId],
                        prices[p.productId],
                        externalPrices[p.productId],
                        reservedAgreementPrices[p.productId]
                    ] as const
            )
            .filter(
                ([p, stock, agreementPrice, priceToBeSaved, refPrice, refExtPrice]) =>
                    (!selectedWarehouse || p.warehouseIds?.includes(selectedWarehouse ?? 0)) &&
                    (showAllArticles || agreementPrice != null) &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    includes(filters.productName, p.name) &&
                    includes(filters.country, p.countryOfOrigin) &&
                    startsWith(filters.stock, stock?.toString()) &&
                    startsWith(filters.price, (priceToBeSaved?.price ?? agreementPrice?.price)?.toString()) &&
                    includes(filters.comment, priceToBeSaved?.comment ?? agreementPrice?.comment) &&
                    startsWith(
                        filters.extPrice,
                        (priceToBeSaved?.extPrice ?? agreementPrice?.externalPrice)?.toString()
                    ) &&
                    startsWith(filters.refPrice, refPrice?.price?.toString()) &&
                    startsWith(filters.refExtPrice, refExtPrice?.salesPrice?.toString())
            )
            .map(
                ([
                    product,
                    stock,
                    agreementPrice,
                    priceToBeSaved,
                    refPrice,
                    refExtPrice,
                    reservedAgreementPriceIds
                ]) => {
                    const agreementPriceRow: AgreementPriceRow = {
                        id: product.productId.toString(),
                        productId: product.productId,
                        productName: product.name,
                        country: product.countryOfOrigin ?? '',
                        unit: product.priceUnit ?? '',
                        purchaser: product.purchaser ?? '',
                        agreementPriceId: agreementPrice?.agreementPriceId ?? 0,
                        comment: '',
                        price: null,
                        priceChangeDirection: PriceChangeDirection.Unaffected,
                        refExtPrice: null,
                        refPrice: null,
                        stock: stock ?? null,
                        priceToBeSaved: priceToBeSaved?.price,
                        extPriceToBeSaved: priceToBeSaved?.extPrice,
                        reservedAgreementPriceIds: reservedAgreementPriceIds ?? [],
                        isActive: true,
                        isInactive: false
                    };
                    if (agreementPrice != null) {
                        agreementPriceRow.id += agreementPrice.agreementPriceId.toString();
                        agreementPriceRow.agreementPriceId = agreementPrice.agreementPriceId;
                        agreementPriceRow.price = agreementPrice.price;
                        agreementPriceRow.extPrice = agreementPrice.externalPrice;
                        agreementPriceRow.updatedBy = agreementPrice.updatedBy ?? '';
                        agreementPriceRow.comment = priceToBeSaved?.comment ?? agreementPrice.comment;
                        if (agreementPrice.updatedDate != null) {
                            agreementPriceRow.updatedDate = new Date(agreementPrice.updatedDate);
                        }
                        agreementPriceRow.discount = agreementPrice.discount;
                        agreementPriceRow.extDiscount = agreementPrice.externalDiscount;
                        agreementPriceRow.isActive = agreementPrice.isActive;
                        agreementPriceRow.isInactive = !agreementPrice.isActive;
                    }

                    if (refPrice != null) {
                        agreementPriceRow.refPrice = refPrice.price;
                    }

                    if (refExtPrice != null) {
                        agreementPriceRow.refExtPrice = refExtPrice.salesPrice ?? null;
                    }

                    return agreementPriceRow;
                }
            );

        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        return orderBy(agreementPriceRows, [o => o[sorting.column] || nullOrderPlacement], sorting.direction);
    }
);
